import { FC } from 'react';
import cn from 'classnames';
import styled from 'styled-components';

import { ButtonIw } from '@/_pages/interwill/components/ButtonIw';
import { ButtonIwText } from '@/_pages/interwill/components/ButtonIwText';
import { useIwExecutionStore } from '@/entities/interwills';
import { COLORS } from '@/shared/constants/colors';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { WillApprovedModalProps } from '@/widgets/modals/types';

import { ModalContainerV2 } from '../containers/ModalContainerV2';

const STEPS_COUNT = 4;
const CURRENT_STEP = 1;

export const WillApprovedModal: FC<WillApprovedModalProps> = ({ onConfirm }) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'willApproved' });
  const { closeModal } = useModalsStore();
  const { willNumber } = useIwExecutionStore();

  return (
    <ModalContainerV2 title={t('title')}>
      <Body>
        <Text>
          <b>{t('documentId')}:</b> {willNumber}
        </Text>
        <Text>{t.rich('description1')}</Text>
        <ListContainer>
          <ListTitle>{t('steps.title')}:</ListTitle>
          <ListItems>
            {new Array(STEPS_COUNT).fill(null).map((_, index) => (
              <ListItem key={index} className={cn({ active: CURRENT_STEP > index })}>
                <ItemIndex className={cn({ active: CURRENT_STEP > index })}>{index + 1}</ItemIndex>
                <ListText>{t(`steps.items.${index}`)}</ListText>
              </ListItem>
            ))}
          </ListItems>
        </ListContainer>
        <Text>{t.rich('description2')}</Text>
      </Body>
      <Buttons>
        <StyledTextButton onClick={closeModal}>{t('btnClose')}</StyledTextButton>
        <ButtonIw onClick={onConfirm}>{t('btnConfirm')}</ButtonIw>
      </Buttons>
    </ModalContainerV2>
  );
};

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px 0px;
`;

const Text = styled.p`
  font-size: 14px;
  line-height: 20px;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 16px;
  font-size: 14px;
  line-height: 20px;
`;

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ListTitle = styled(Text)`
  font-weight: 700;
`;

const ListItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  position: relative;
  font-weight: 700;

  &::before {
    content: '';
    position: absolute;
    width: 1px;
    height: 100%;
    background: ${COLORS.systemDark100};
    left: 10px;
    translate: -50% 0;
  }
`;

const ListItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: ${COLORS.systemDark200};

  &.active {
    color: ${COLORS.systemSky200};
  }
`;

const ListText = styled(Text)`
  font-size: 12px;
  line-height: 16px;
`;

const StyledTextButton = styled(ButtonIwText)`
  &&& {
    font-weight: 700;
  }
`;

const ItemIndex = styled.div`
  font-size: 12px;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${COLORS.white};
  z-index: 1;
  color: inherit;
  border: 1px solid ${COLORS.systemDark200};
  font-weight: 400;

  &.active {
    border: 1px solid ${COLORS.systemSky200};
  }
`;
